import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { describe } from "./EventLog";
import { Mark } from "./Mark";
import { Markdown } from "./Markdown";
import { Button, ErrorNote, InlineNote } from "./primitives";
import { SpendLine, useActionCeiling } from "./SpendConfirm";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { taskLog } from "../lib/tasklog";
import type {
  FinetuneRequest,
  FinetuneTarget,
  Manifest,
  RunEvent,
  RunTask,
} from "../lib/types";

/** The markdown a rendering was made from: `report.html` is `report.md`. */
export function markdownOf(name: string): string {
  return name.replace(/\.(html|docx)$/i, ".md");
}

/** How the panel names a part: "Figure 10 · Market share", "Chapter · Market". */
export function partName(target: {
  kind: string;
  label: string;
  caption?: string;
  level?: number;
}): string {
  if (target.kind === "figure") {
    return `${target.caption || "Figure"}${target.label ? ` · ${target.label}` : ""}`;
  }
  return `${(target.level ?? 2) <= 2 ? "Chapter" : "Section"} · ${target.label}`;
}

/**
 * Finetuning one part of a deliverable: a chapter, a section or a figure.
 *
 * Opened by the page itself — the reader pressed *Finetune* on a heading or a
 * figure in the document beside it — and it takes the evidence rail's place.
 * What it has to be honest about is the same as the discussion's: that a
 * request **costs money**, so the ceiling is stated beside the button, and
 * **what the agent is doing while the reader waits**. The second is the
 * point of the panel: the agent's own sentences and its tool calls arrive
 * from the run's events as it works — the plan first, then a line a step —
 * and the document is reloaded with the part marked once it has been
 * rendered again.
 *
 * Only the part changes, and that is the server's rule, not this panel's:
 * the one tool the agent has that writes the document is bound to the part.
 */
export function FinetunePanel({
  runId,
  name,
  target,
  events,
  onClose,
  onLanded,
}: {
  runId: string;
  /** The HTML file being read. */
  name: string;
  target: FinetuneTarget;
  /** The run's events — where the work shows while it is going on. */
  events: RunEvent[];
  onClose: () => void;
  /** The document changed: reload it and mark the part again. */
  onLanded: () => void;
}) {
  const queryClient = useQueryClient();
  const ceiling = useActionCeiling("finetune");
  const [request, setRequest] = useState("");
  const [sent, setSent] = useState("");
  const source = markdownOf(name);

  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.run(runId),
    refetchInterval: (query) =>
      query.state.data?.task?.status === "running" ? 2_500 : false,
  });
  const task: RunTask | null = run.data?.task ?? null;
  const busy = task?.status === "running";
  const mine = busy && task?.kind === "finetune";

  const record = useQuery({
    queryKey: ["finetunes", runId],
    queryFn: () => api.finetunes(runId),
    refetchInterval: (query) => (query.state.data?.running || mine ? 3_000 : false),
  });
  const history = (record.data?.requests ?? []).filter((r) => r.name === source);
  const done = history.filter((r) => r.status !== "running");
  const running = history.find((r) => r.status === "running") ?? null;

  const ask = useMutation({
    mutationFn: () => api.finetune(runId, name, target, request.trim()),
    onSuccess: (started) => {
      setSent(request.trim());
      setRequest("");
      // The task the server just started, on the run at once: the panel shows
      // it working from the click rather than from the next poll.
      queryClient.setQueryData<Manifest>(
        ["run", runId],
        (old) => (old ? { ...old, task: started } : old),
      );
      queryClient.invalidateQueries({ queryKey: ["finetunes", runId] });
    },
  });

  const undo = useMutation({
    mutationFn: (id: string) => api.undoFinetune(runId, id),
    onSuccess: (data) => {
      queryClient.setQueryData(["finetunes", runId], data);
      landed();
    },
  });

  function landed() {
    queryClient.invalidateQueries({ queryKey: ["artefacts", runId] });
    queryClient.invalidateQueries({ queryKey: ["artefact", runId] });
    onLanded();
  }

  // The work ending is a *transition* this panel watched start. A finished
  // task stays on the run for two minutes, and a reader opening the panel in
  // that time must not have their page reloaded by work they did not watch.
  const watching = useRef(false);
  useEffect(() => {
    if (mine) {
      watching.current = true;
      return;
    }
    if (!watching.current) return;
    watching.current = false;
    queryClient.invalidateQueries({ queryKey: ["finetunes", runId] });
    landed();
    // `landed` is recreated each render and reads only stable values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine]);

  const log = mine ? taskLog(events, "finetune", task?.started_at) : [];
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length, done.length]);

  const blocked = busy && !mine;
  const waiting = ask.isPending || mine;
  const canSend = request.trim().length > 0 && !waiting && !blocked;

  return (
    <aside
      aria-label="Finetune"
      className="flex h-full min-h-0 flex-col border-t border-line bg-surface lg:border-l lg:border-t-0"
    >
      <div className="flex items-start gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="label">Finetune</p>
          <p className="mt-1 truncate text-sm font-medium text-ink" title={partName(target)}>
            {partName(target)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-sm px-2 py-1 text-xs text-ink-2 hover:bg-surface-2 hover:text-ink"
          aria-label="Close the finetune panel"
        >
          Close
        </button>
      </div>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {done.length === 0 && !waiting ? (
          <Opening target={target} />
        ) : (
          <ul className="space-y-5">
            {done.map((entry) => (
              <Exchange
                key={entry.id}
                runId={runId}
                entry={entry}
                undoing={undo.isPending && undo.variables === entry.id}
                onUndo={() => undo.mutate(entry.id)}
                canUndo={!busy && !undo.isPending}
              />
            ))}
          </ul>
        )}

        {waiting && (
          <div className={done.length ? "mt-5" : ""}>
            <Asked text={running?.request || sent} part={partName(target)} />
            <Working log={log} elapsed={mine ? (task?.elapsed_s ?? 0) : 0} />
          </div>
        )}
        {undo.isError && (
          <div className="mt-3">
            <ErrorNote error={undo.error} />
          </div>
        )}
      </div>

      <div className="border-t border-line px-4 py-3">
        {ask.error && <ErrorNote error={ask.error} />}
        {blocked && (
          <InlineNote tone="neutral" className="mb-2">
            {task?.kind === "deck"
              ? "A deck is being built from this run. Finetune once it is done."
              : "Other work is running on this run. Finetune once it is done."}
          </InlineNote>
        )}
        <textarea
          className="field"
          rows={3}
          placeholder={
            target.kind === "figure"
              ? "What should change about this figure? “Show it in 3D”, “add the 2025 estimate”…"
              : "What should change here? Rewrite, add, shorten, or ask for a deep dive…"
          }
          value={request}
          disabled={waiting}
          onChange={(e) => setRequest(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, shift+enter breaks the line — as in the discussion.
            if (e.key === "Enter" && !e.shiftKey && canSend) {
              e.preventDefault();
              ask.mutate();
            }
          }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Button variant="primary" size="sm" disabled={!canSend} onClick={() => ask.mutate()}>
            {waiting ? "Working…" : "Finetune"}
          </Button>
          {/* Before the request, not after it: each one opens a model session
              with a ceiling of its own and charges no credit (#124). */}
          <span className="text-xs text-muted">
            <SpendLine cost={ceiling} />
          </span>
        </div>
      </div>
    </aside>
  );
}

function Opening({ target }: { target: FinetuneTarget }) {
  return (
    <div className="py-6 text-sm text-ink-2">
      <p>
        Say what should change about{" "}
        {target.kind === "figure" ? "this figure" : `“${target.label}”`}. Only this part of the
        document is changed; everything around it stays as it is.
      </p>
      <p className="mt-3 text-xs text-muted">
        The agent reads the whole run, may research what the run does not hold — every new
        fact is cited — and says what it is doing as it goes. The HTML and Word files are
        rendered again when it is done, and the change can be undone.
      </p>
    </div>
  );
}

function Asked({ text, part }: { text: string; part: string }) {
  return (
    <div>
      <div className="flex justify-end">
        <div className="max-w-[90%] whitespace-pre-wrap rounded-lg bg-surface-2 px-3 py-2 text-sm">
          {text}
        </div>
      </div>
      <p className="mt-1 truncate text-right text-xs text-muted">{part}</p>
    </div>
  );
}

/**
 * What the agent is doing, as it does it.
 *
 * Its own sentences as prose — the plan, then a line before each step — and
 * its calls and the job's own steps as the deck frame shows them, through
 * `EventLog`'s `describe`, so the two cannot read one row two ways.
 */
function Working({ log, elapsed }: { log: RunEvent[]; elapsed: number }) {
  return (
    <div role="status" aria-live="polite" className="mt-4 space-y-2">
      {log.length === 0 ? (
        <p className="text-sm text-ink-2">Starting — reading the run first.</p>
      ) : (
        log.map((event, index) =>
          event.type === "assistant.text" ? (
            <p key={index} className="text-sm text-ink">
              {String(event.preview ?? "")}
            </p>
          ) : (
            <p
              key={index}
              className={`truncate font-mono text-xs ${describe(event).tone}`}
              title={describe(event).text}
            >
              {describe(event).text}
            </p>
          ),
        )
      )}
      <div className="flex items-center gap-2 pt-1">
        <Mark size={32} animate loop />
        <span className="text-xs text-muted">
          Working · <span className="tabular">{Math.round(elapsed)}s</span>
        </span>
      </div>
    </div>
  );
}

function Exchange({
  runId,
  entry,
  undoing,
  canUndo,
  onUndo,
}: {
  runId: string;
  entry: FinetuneRequest;
  undoing: boolean;
  canUndo: boolean;
  onUndo: () => void;
}) {
  const outcome =
    entry.status === "done"
      ? entry.undone
        ? "Undone — the version from before is back."
        : `Changed ${entry.changed.join(", ")}; the HTML and Word were rendered again.`
      : entry.status === "unchanged"
        ? "Nothing in the document changed."
        : entry.status === "interrupted"
          ? null
          : "Not finished.";

  return (
    <li>
      <Asked text={entry.request} part={partName(entry.target)} />
      {entry.said && (
        <div className="mt-3 text-sm">
          <Markdown content={entry.said} resolveHref={(href) => api.siblingUrl(runId, href)} />
        </div>
      )}
      {entry.error && (
        <InlineNote tone="critical" className="mt-2">
          {entry.error}
        </InlineNote>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted">
        {outcome && <span>{outcome}</span>}
        {entry.cost_usd > 0 && (
          <span className="font-mono tabular-nums">{money(entry.cost_usd)}</span>
        )}
        {entry.undoable && (
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            className="text-ink-2 underline underline-offset-2 hover:text-ink disabled:opacity-50"
            title="Put back the version from before this finetune, and render it again"
          >
            {undoing ? "Undoing…" : "Undo"}
          </button>
        )}
      </div>
    </li>
  );
}
